import { MapPin, Star, DollarSign, Users } from 'lucide-react';
import type { ProviderCardItem } from '@/types/provider-card';
import styles from './ProviderCard.module.css';

const GRADIENT_COLORS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
];

function renderStars(rating: number | null) {
  if (rating === null) return null;
  const full = Math.round(rating);
  return (
    <span className={styles.stars} aria-label={`Rating: ${rating}`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < full ? styles.starFilled : styles.starEmpty}>
          &#9733;
        </span>
      ))}
    </span>
  );
}

interface Props {
  provider: ProviderCardItem;
  /** 'grid' for wizard cards, 'compact' for chat horizontal scroll */
  mode?: 'grid' | 'compact';
  isSelected?: boolean;
  onToggle?: () => void;
  onViewDetails: () => void;
}

export const ProviderCard = ({
  provider,
  mode = 'grid',
  isSelected = false,
  onToggle,
  onViewDetails,
}: Props) => {
  const idHash = String(provider.id).split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const gradient = GRADIENT_COLORS[idHash % GRADIENT_COLORS.length];

  if (mode === 'compact') {
    return (
      <div
        className={`${styles.card} ${styles.compact}`}
        onClick={onViewDetails}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onViewDetails();
        }}
      >
        {/* Image */}
        <div className={styles.imageArea}>
          {provider.image ? (
            <img src={provider.image} alt={provider.name} className={styles.image} />
          ) : (
            <div className={styles.placeholder} style={{ background: gradient }}>
              <span className={styles.placeholderLetter}>
                {provider.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        <div className={styles.info}>
          {/* Header: name + rating */}
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <div>
              <h3 className={styles.name} title={provider.name}>
                {provider.name}
              </h3>
              {provider.services.length > 0 && (
                <span className={styles.compactServiceBadge}>{provider.services[0].name}</span>
              )}
            </div>
            {provider.rating !== null && provider.rating > 0 && (
              <div className={styles.compactRating}>
                <Star size={14} className={styles.compactRatingIcon} />
                <span className={styles.compactRatingValue}>{provider.rating}</span>
              </div>
            )}
          </div>

          {provider.description && <p className={styles.compactDesc}>{provider.description}</p>}

          <div className={styles.compactDetails}>
            {(provider.city || provider.district) && (
              <div className={styles.detailRow}>
                <MapPin className={styles.detailIcon} />
                <span className={styles.detailText}>
                  {[provider.district, provider.city].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
            {provider.capacity !== null && provider.capacity > 0 && (
              <div className={styles.detailRow}>
                <Users className={styles.detailIcon} />
                <span>Cap: {provider.capacity} pax</span>
              </div>
            )}
            <div className={`${styles.detailRow} ${styles.priceRow}`}>
              <DollarSign className={styles.detailIcon} />
              <span>{provider.priceDisplay}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid mode (wizard)
  return (
    <div
      className={`${styles.card} ${isSelected ? styles.selected : ''}`}
      onClick={onViewDetails}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onViewDetails();
        if (e.key === ' ' && onToggle) {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <div className={styles.imageArea}>
        {provider.image ? (
          <img src={provider.image} alt={provider.name} className={styles.image} />
        ) : (
          <div className={styles.placeholder} style={{ background: gradient }}>
            <span className={styles.placeholderLetter}>
              {provider.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        {onToggle && (
          <div
            className={styles.checkbox}
            role="checkbox"
            aria-checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            title={isSelected ? 'Remove from selection' : 'Add to selection'}
          >
            {isSelected && <span className={styles.checkmark}>&#10003;</span>}
          </div>
        )}
        {provider.serviceCount > 0 && (
          <span className={styles.serviceBadge}>
            {provider.serviceCount} service{provider.serviceCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className={styles.info}>
        <h3 className={styles.name}>{provider.name}</h3>
        <div className={styles.meta}>
          <span className={styles.location}>
            {provider.district ? `${provider.district}, ${provider.city}` : provider.city}
          </span>
          {provider.rating !== null && renderStars(provider.rating)}
        </div>
        {provider.services.length > 0 && (
          <p className={styles.serviceNames}>
            {provider.services
              .slice(0, 3)
              .map((s) => s.name)
              .join(', ')}
            {provider.services.length > 3 && ` +${provider.services.length - 3}`}
          </p>
        )}
        <p className={styles.price}>{provider.priceDisplay}</p>
      </div>
    </div>
  );
};
